import React from 'react';
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { StatsigProvider } from '@statsig/react-bindings';
import theme from './styles/theme';
import GlobalStyle from './styles/global';
import Signin from './features/Auth/Signin';
import SsoRedirect from './features/Auth/SsoRedirect';
import PublicRoute from './services/PublicRoute';
import PrivateRoute from './services/PrivateRoute';
import PurchaseRoute from './services/PurchaseRoute';
import PrivateLightRoute from './services/PrivateLightRoute';
import ForgotPassword from './features/Auth/ForgotPassword';
import ResetPassword from './features/Auth/ResetPassword';
import SignupStepOne from './features/Auth/Signup/SignupStepOne';
import SignupStepTwo from './features/Auth/Signup/SignupStepTwo';
import SignupStepThree from './features/Auth/Signup/SignupStepThree';
import SelectPackage from './features/Purchase/SelectPackage';
import AddPayment from './features/Purchase/AddPayment/AddPayment';
import Summary from './features/Purchase/Summary/Summary';
import Dashboard from './features/Dashboard';
import MultiAsset from './features/Purchase/MultiAsset/MultiAsset';
import PageView from './components/PageView/PageView';
import URLInterceptor from './utils/URLInterceptor';
import Billing from './features/Billing/Billing';
import SummaryEnterprise from './features/Purchase/Summary/SummaryEnterprise';
import Library from './features/Library/Library';
import Home from './features/Home';
import MultiCompanyAccess from './features/MultiCompanyAccess';
import { getSessionUser } from './features/Auth';
import TrackingReportPrivateRoute from './services/TrackingReportPrivateRoute';

const App: React.FC = () => {
  const sessionUser = getSessionUser();
  const statsigUser = sessionUser
    ? {
        userID: String(sessionUser.staffid ?? 'anonymous'),
        email: sessionUser.email,
      }
    : { userID: 'anonymous' };

  return (
    <StatsigProvider
      sdkKey={process.env.REACT_APP_STATSIG_CLIENT_API ?? ''}
      user={statsigUser}
      options={{
        disableStatsigEncoding: true,
        environment: { tier: process.env.REACT_APP_STATSIG_ENV },
      }}
    >
      <Router>
        <URLInterceptor>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <PageView />
            <Routes>
              {/* Not Found */}
              <Route path="/*" element={<Navigate to="/" />} />
              {/* Private routes */}
              <Route path="/" element={<PrivateRoute component={Home} />} />
              <Route path="/home" element={<PrivateRoute component={Home} />} />
              <Route
                path="/dashboard"
                element={<PrivateRoute component={Dashboard} />}
              />
              <Route
                path="/trackingreport"
                element={<TrackingReportPrivateRoute component={Dashboard} />}
              />
              <Route
                path="/billing"
                element={<PrivateRoute component={Billing} />}
              />
              {/* Multi-company route (private) */}
              <Route
                path="/multi-company"
                element={<PrivateLightRoute component={MultiCompanyAccess} />}
              />
              <Route
                path="/library"
                element={<PrivateRoute component={Library} />}
              />
              <Route path="payment">
                <Route
                  path="multiasset"
                  element={<PurchaseRoute component={MultiAsset} />}
                />
                <Route
                  path="selectpackage"
                  element={<PurchaseRoute component={SelectPackage} />}
                />
                <Route
                  path="addpayment"
                  element={<PurchaseRoute component={AddPayment} />}
                />
                <Route
                  path="summary"
                  element={<PurchaseRoute component={Summary} />}
                />
                <Route
                  path="enterprise/summary"
                  element={<PurchaseRoute component={SummaryEnterprise} />}
                />
              </Route>
              {/* Public routes */}
              <Route path="auth">
                <Route
                  path="signup"
                  element={<PublicRoute component={SignupStepOne} />}
                />
                <Route
                  path="signupsteptwo"
                  element={<PublicRoute component={SignupStepTwo} />}
                />
                <Route
                  path="signupstepthree"
                  element={<PublicRoute component={SignupStepThree} />}
                />
                <Route
                  path="signin"
                  element={<PublicRoute component={Signin} />}
                />
                <Route
                  path="/auth/sso/callback"
                  element={<PublicRoute component={SsoRedirect} />}
                />
                <Route
                  path="forgot_password"
                  element={<PublicRoute component={ForgotPassword} />}
                />
                <Route
                  path="reset_password/:key"
                  element={<PublicRoute component={ResetPassword} />}
                />
              </Route>
            </Routes>
          </ThemeProvider>
        </URLInterceptor>
      </Router>
    </StatsigProvider>
  );
};

export default App;
